import {
  parseGeniusDeeplink,
  type GeniusDeeplinkParams,
} from "./parseGeniusDeeplink";
import {
  clearGeniusDeeplinkNavLock,
  isGeniusDeeplinkNavLockActive,
  isGeniusMobileVerified,
} from "./geniusDeeplinkNavLock";
import { notifyGeniusDeeplinkFlowDismissed } from "./geniusDeeplinkDismiss";
import { clearGeniusLogoutConfirmPending } from "./geniusLogoutConfirm";
import type { ProfilesData } from "../../../store/useAuthStore";
import { useAuthStore } from "../../../store/useAuthStore";
import { ROUTESWEB } from "@repo/utils";

/**
 * Fresh Genius URL open (new tab / pasted link) must not inherit sticky
 * continue/nav-lock flags from a prior OTP-pending session — that skips
 * signed-token auth and leaves the welcome screen loading forever (white page).
 */
export function resetStickyGeniusResumeForFreshOpen(): void {
  markGeniusFlowContinued(false);
  markGeniusFlowPhase("idle");
  clearGeniusDeeplinkNavLock();
}

const GENIUS_DEEPLINK_SESSION_KEY = "genius_deeplink_flow_active";
const GENIUS_DEEPLINK_AUTO_RESUME_KEY = "genius_deeplink_auto_resume";
const GENIUS_DEEPLINK_HAS_CONTINUED_KEY = "genius_deeplink_has_continued";
const GENIUS_DEEPLINK_FLOW_PHASE_KEY = "genius_deeplink_flow_phase";
const GENIUS_DEEPLINK_ORIGINAL_URL_KEY = "genius_deeplink_original_url";
/** Set when we send the user to a blank leave page; next base-URL open resumes Genius. */
const GENIUS_EXPECT_RESUME_ON_SHELL_KEY = "genius_expect_resume_on_shell";
/** sessionStorage: prior signed token snapshot captured before main.tsx persist. */
const GENIUS_PRIOR_TOKEN_SNAPSHOT_KEY = "genius_prior_token_snapshot";
/** sessionStorage: this tab is mid shell→Genius resume (not a fresh pasted link). */
const GENIUS_SHELL_RESUME_TAB_KEY = "genius_shell_resume_tab";
/**
 * Survives completeGeniusDeeplinkFlow — same one-time signed token must not be
 * re-exchanged when the student re-opens the Genius link after OTP / report.
 */
const GENIUS_CONSUMED_SIGNED_TOKEN_KEY = "genius_consumed_signed_token";

export function markGeniusSignedTokenConsumed(signedToken: string): void {
  if (!signedToken) return;
  writeLocal(GENIUS_CONSUMED_SIGNED_TOKEN_KEY, signedToken);
}

export function readGeniusConsumedSignedToken(): string | null {
  return readLocal(GENIUS_CONSUMED_SIGNED_TOKEN_KEY);
}

export function isGeniusSignedTokenMarkedConsumed(
  signedToken: string,
): boolean {
  return (
    Boolean(signedToken) && readGeniusConsumedSignedToken() === signedToken
  );
}

function rememberConsumedSignedTokenFromSession(): void {
  const token =
    getActiveGeniusAuthToken() ??
    readPersistedGeniusDeeplinkParams()?.signedToken ??
    null;
  if (token) {
    markGeniusSignedTokenConsumed(token);
  }
}

export function snapshotGeniusTokenBeforePersist(
  incomingSignedToken: string,
): void {
  const priorSignedToken =
    readPersistedGeniusDeeplinkParams()?.signedToken ?? null;
  try {
    sessionStorage.setItem(
      GENIUS_PRIOR_TOKEN_SNAPSHOT_KEY,
      JSON.stringify({ priorSignedToken, incomingSignedToken }),
    );
  } catch {
    // ignore
  }
}

export function readGeniusTokenPersistSnapshot(): {
  priorSignedToken: string | null;
  incomingSignedToken: string;
} | null {
  try {
    const raw = sessionStorage.getItem(GENIUS_PRIOR_TOKEN_SNAPSHOT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as {
      priorSignedToken: string | null;
      incomingSignedToken: string;
    };
  } catch {
    return null;
  }
}

export function clearGeniusTokenPersistSnapshot(): void {
  try {
    sessionStorage.removeItem(GENIUS_PRIOR_TOKEN_SNAPSHOT_KEY);
  } catch {
    // ignore
  }
}

/** Token persisted before this page load (before main.tsx overwrote storage). */
export function getPriorPersistedTokenBeforePageLoad(): string | null {
  return readGeniusTokenPersistSnapshot()?.priorSignedToken ?? null;
}

/**
 * True when this one-time signed token was already consumed in the current
 * Genius session — safe to re-land without re-exchange.
 * Must use the pre-persist snapshot; reading storage after main.tsx persist
 * makes every URL look like a same-token re-open.
 */
export function isGeniusSignedTokenAlreadyConsumed(
  signedToken: string,
): boolean {
  // Durable marker — survives completeGeniusDeeplinkFlow / release session.
  if (isGeniusSignedTokenMarkedConsumed(signedToken)) {
    return true;
  }

  const priorPersistedToken = getPriorPersistedTokenBeforePageLoad();
  const priorActiveToken = getActiveGeniusAuthToken();
  const priorPhase = getGeniusFlowPhase();

  const sameAsPriorPersisted =
    priorPersistedToken != null && priorPersistedToken === signedToken;

  if (!sameAsPriorPersisted) {
    return (
      priorActiveToken === signedToken &&
      (priorPhase === "completed" ||
        priorPhase === "continuing" ||
        priorPhase === "ready")
    );
  }

  return (
    priorPhase === "completed" ||
    priorPhase === "continuing" ||
    priorPhase === "ready" ||
    hasGeniusFlowContinued()
  );
}

export function clearGeniusShellResumeFlags(): void {
  try {
    sessionStorage.removeItem(GENIUS_SHELL_RESUME_TAB_KEY);
  } catch {
    // ignore
  }
  removeLocal(GENIUS_EXPECT_RESUME_ON_SHELL_KEY);
}

/**
 * Call from main.tsx before persisting a Genius URL.
 * Different signed token → fresh open; same token re-open → keep phase.
 */
export function prepareGeniusDeeplinkPageLoad(
  incoming: GeniusDeeplinkParams,
  options?: { isShellResumeEntry?: boolean },
): void {
  const persisted = readPersistedGeniusDeeplinkParams();
  const isDifferentToken =
    persisted?.signedToken != null &&
    persisted.signedToken !== incoming.signedToken;

  if (isDifferentToken) {
    clearGeniusShellResumeFlags();
    resetStickyGeniusResumeForFreshOpen();
    resetActiveGeniusAuthToken();
  } else if (!options?.isShellResumeEntry) {
    const isSameTokenReopen =
      persisted?.signedToken != null &&
      persisted.signedToken === incoming.signedToken;
    if (!isSameTokenReopen) {
      resetStickyGeniusResumeForFreshOpen();
    }
  }

  snapshotGeniusTokenBeforePersist(incoming.signedToken);
}

/** Auth/login paths — never trap the user in Genius; allow normal login. */
const GENIUS_AUTH_ESCAPE_PATHS = [
  `/${ROUTESWEB.AUTH.INTRO}`,
  `/${ROUTESWEB.AUTH.LOGIN}`,
  `/${ROUTESWEB.AUTH.PHONE_LOGIN}`,
  `/${ROUTESWEB.AUTH.OTP_VERIFICATION}`,
  `/${ROUTESWEB.AUTH.USERNAME_LOGIN}`,
  `/${ROUTESWEB.AUTH.FORGOT_PASSWORD}`,
  `/${ROUTESWEB.AUTH.RESET_PASSWORD}`,
  `/${ROUTESWEB.AUTH.MULTIPLE_ACCOUNTS}`,
  `/${ROUTESWEB.AUTH.SIGNUP}`,
  `/${ROUTESWEB.AUTH.ADMISSION_ID_FLOW}`,
  `/${ROUTESWEB.AUTH.ACTIVATION_CODE}`,
  `/${ROUTESWEB.AUTH.CHANGE_PHONE_INSTRUCTIONS}`,
  `/${ROUTESWEB.AUTH.CHANGE_PHONE_NEW_NUMBER}`,
  `/${ROUTESWEB.AUTH.CHANGE_PHONE_VERIFY}`,
] as const;

export type GeniusFlowPhase =
  | "idle"
  | "authenticating"
  | "ready"
  | "continuing"
  | "completed";

let activeGeniusAuthToken: string | null = null;

const GENIUS_URL_BOOTSTRAP_DEBOUNCE_MS = 1_500;
let lastBootstrappedGeniusUrl: string | null = null;
let lastBootstrappedGeniusAt = 0;

export function isGeniusFlowInProgress(): boolean {
  const phase = getGeniusFlowPhase();
  return (
    phase === "authenticating" || phase === "ready" || phase === "continuing"
  );
}

/**
 * Skip only duplicate deliveries of the same Genius URL within a short window,
 * or mid-auth for the same one-time signed token.
 *
 * Never skip when the student re-opens the same link after completion or with
 * an existing session — that must re-land, not leave a white loader forever.
 */
export function shouldSkipGeniusUrlBootstrap(url: string): boolean {
  const parsed = parseGeniusDeeplink(url);
  if (!parsed) return false;

  const phase = getGeniusFlowPhase();
  if (phase === "completed") {
    return false;
  }

  const persisted = readPersistedGeniusDeeplinkParams();
  const samePersistedToken =
    persisted?.signedToken != null &&
    persisted.signedToken === parsed.signedToken;
  const hasSession = useAuthStore.getState().token != null;

  if (
    samePersistedToken &&
    hasSession &&
    isGeniusSignedTokenAlreadyConsumed(parsed.signedToken)
  ) {
    return false;
  }

  const now = Date.now();
  if (
    lastBootstrappedGeniusUrl === url &&
    now - lastBootstrappedGeniusAt < GENIUS_URL_BOOTSTRAP_DEBOUNCE_MS
  ) {
    return true;
  }

  if (
    activeGeniusAuthToken === parsed.signedToken &&
    isGeniusFlowInProgress()
  ) {
    return true;
  }

  return false;
}

export function markGeniusUrlBootstrapped(url: string): void {
  lastBootstrappedGeniusUrl = url;
  lastBootstrappedGeniusAt = Date.now();
}

export function resetGeniusUrlBootstrapTracking(): void {
  lastBootstrappedGeniusUrl = null;
  lastBootstrappedGeniusAt = 0;
}

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function removeLocal(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function getActiveGeniusAuthToken(): string | null {
  return activeGeniusAuthToken;
}

export function setActiveGeniusAuthToken(token: string | null): void {
  activeGeniusAuthToken = token;
}

export function resetActiveGeniusAuthToken(): void {
  activeGeniusAuthToken = null;
}

export function getGeniusFlowPhase(): GeniusFlowPhase {
  const phase = readLocal(GENIUS_DEEPLINK_FLOW_PHASE_KEY);
  if (
    phase === "authenticating" ||
    phase === "ready" ||
    phase === "continuing" ||
    phase === "completed"
  ) {
    return phase;
  }
  return "idle";
}

export function markGeniusFlowPhase(phase: GeniusFlowPhase): void {
  if (phase === "completed") {
    rememberConsumedSignedTokenFromSession();
  }
  if (phase === "idle") {
    removeLocal(GENIUS_DEEPLINK_FLOW_PHASE_KEY);
    return;
  }
  writeLocal(GENIUS_DEEPLINK_FLOW_PHASE_KEY, phase);
}

export function hasGeniusFlowContinued(): boolean {
  return readLocal(GENIUS_DEEPLINK_HAS_CONTINUED_KEY) === "1";
}

export function markGeniusFlowContinued(value = true): void {
  if (value) {
    writeLocal(GENIUS_DEEPLINK_HAS_CONTINUED_KEY, "1");
  } else {
    removeLocal(GENIUS_DEEPLINK_HAS_CONTINUED_KEY);
  }
}

export function persistGeniusDeeplinkParams(
  params: GeniusDeeplinkParams,
  originalUrl?: string,
): void {
  writeLocal(GENIUS_DEEPLINK_SESSION_KEY, JSON.stringify(params));
  writeLocal(GENIUS_DEEPLINK_AUTO_RESUME_KEY, "1");
  if (originalUrl) {
    writeLocal(GENIUS_DEEPLINK_ORIGINAL_URL_KEY, originalUrl);
  }
  // Also keep sessionStorage copy for same-tab flows.
  try {
    sessionStorage.setItem(GENIUS_DEEPLINK_SESSION_KEY, JSON.stringify(params));
  } catch {
    // ignore
  }
}

export function readPersistedGeniusOriginalUrl(): string | null {
  return readLocal(GENIUS_DEEPLINK_ORIGINAL_URL_KEY);
}

export function readPersistedGeniusDeeplinkParams(): GeniusDeeplinkParams | null {
  try {
    const raw =
      readLocal(GENIUS_DEEPLINK_SESSION_KEY) ??
      sessionStorage.getItem(GENIUS_DEEPLINK_SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as GeniusDeeplinkParams;
  } catch {
    return null;
  }
}

export function clearPersistedGeniusDeeplinkParams(): void {
  removeLocal(GENIUS_DEEPLINK_SESSION_KEY);
  removeLocal(GENIUS_DEEPLINK_AUTO_RESUME_KEY);
  removeLocal(GENIUS_DEEPLINK_HAS_CONTINUED_KEY);
  removeLocal(GENIUS_DEEPLINK_FLOW_PHASE_KEY);
  removeLocal(GENIUS_DEEPLINK_ORIGINAL_URL_KEY);
  removeLocal(GENIUS_EXPECT_RESUME_ON_SHELL_KEY);
  try {
    sessionStorage.removeItem(GENIUS_DEEPLINK_SESSION_KEY);
  } catch {
    // ignore
  }
}

export function isGeniusDeeplinkAutoResumeEnabled(): boolean {
  return readLocal(GENIUS_DEEPLINK_AUTO_RESUME_KEY) === "1";
}

export function markGeniusDeeplinkAutoResumeEnabled(enabled = true): void {
  if (enabled) {
    writeLocal(GENIUS_DEEPLINK_AUTO_RESUME_KEY, "1");
  } else {
    removeLocal(GENIUS_DEEPLINK_AUTO_RESUME_KEY);
  }
}

/**
 * True when this page load came from another page on our origin
 * (typical address-bar edit / in-site navigation), vs external/typed entry.
 * Prefer `markGeniusExpectResumeOnShell` / `consumeGeniusExpectResumeOnShell`
 * for leave→google→base-URL flows — referrer is unreliable on address-bar edits.
 */
export function isGeniusNavigationFromSameOrigin(): boolean {
  try {
    const referrer = document.referrer;
    if (!referrer) return false;
    return new URL(referrer).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Base / home shell paths — while Genius OTP is pending these must ALWAYS
 * resume the deeplink. Never send them to blank (that blocks re-entry).
 * Auth/login paths are NOT shells — see isGeniusAuthEscapePath.
 */
export function isGeniusAppShellPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, "") || "/";
  return (
    path === "/" ||
    path === "/dashboard" ||
    path === "/home" ||
    path === "/play" ||
    path === "/explore"
  );
}

/**
 * Login / intro / signup routes — user must always be able to reach these.
 * Opening them abandons a stuck Genius session (API failure, timeout, etc.).
 */
export function isGeniusAuthEscapePath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, "") || "/";
  return GENIUS_AUTH_ESCAPE_PATHS.some(
    (escapePath) => path === escapePath || path.startsWith(`${escapePath}/`),
  );
}

/**
 * Clear Genius lock/resume when the user is on an auth escape route.
 * No-op if Genius is not pending. Safe to call from AuthGuard / main.
 */
export function abandonGeniusDeeplinkIfAuthEscape(pathname: string): boolean {
  if (!isGeniusAuthEscapePath(pathname)) return false;
  if (
    !isGeniusDeeplinkAutoResumeEnabled() &&
    readPersistedGeniusDeeplinkParams() == null
  ) {
    return false;
  }
  completeGeniusDeeplinkFlow();
  return true;
}

/** Call right before sending the user to the blank leave page (back / URL edit). */
export function markGeniusExpectResumeOnShell(): void {
  writeLocal(GENIUS_EXPECT_RESUME_ON_SHELL_KEY, "1");
}

/** Mark this tab as doing base/shell → Genius resume (survives hard redirect). */
export function markGeniusShellResumeInThisTab(): void {
  try {
    sessionStorage.setItem(GENIUS_SHELL_RESUME_TAB_KEY, "1");
  } catch {
    // ignore
  }
}

export function consumeGeniusShellResumeInThisTab(): boolean {
  try {
    if (sessionStorage.getItem(GENIUS_SHELL_RESUME_TAB_KEY) !== "1") {
      return false;
    }
    sessionStorage.removeItem(GENIUS_SHELL_RESUME_TAB_KEY);
    return true;
  } catch {
    return false;
  }
}

export function hasGeniusShellResumeInThisTab(): boolean {
  try {
    return sessionStorage.getItem(GENIUS_SHELL_RESUME_TAB_KEY) === "1";
  } catch {
    return false;
  }
}

/** True after blank leave; next base-URL open can resume. Not consumed until Genius land. */
export function consumeGeniusExpectResumeOnShell(): boolean {
  if (readLocal(GENIUS_EXPECT_RESUME_ON_SHELL_KEY) !== "1") return false;
  removeLocal(GENIUS_EXPECT_RESUME_ON_SHELL_KEY);
  return true;
}

export function hasGeniusExpectResumeOnShell(): boolean {
  return readLocal(GENIUS_EXPECT_RESUME_ON_SHELL_KEY) === "1";
}

/** Peek: blank → base/shell → Genius resume pending in this tab. */
export function isGeniusShellResumePending(): boolean {
  return hasGeniusShellResumeInThisTab() || hasGeniusExpectResumeOnShell();
}

/**
 * True when this Genius page load came from blank leave → shell/genius resume.
 * Uses sessionStorage (base-URL redirect) or localStorage expect flag for the
 * same persisted signed token (address-bar edit while OTP pending).
 */
export function consumeGeniusDirectResumeAfterShell(
  incomingSignedToken?: string,
): boolean {
  if (consumeGeniusShellResumeInThisTab()) {
    return true;
  }

  if (!hasGeniusExpectResumeOnShell()) {
    return false;
  }

  const persisted = readPersistedGeniusDeeplinkParams()?.signedToken;
  if (incomingSignedToken && persisted && incomingSignedToken !== persisted) {
    consumeGeniusExpectResumeOnShell();
    return false;
  }

  return consumeGeniusExpectResumeOnShell();
}

/** Peek: should resume the same Genius session after blank leave (URL edit/back). */
export function shouldResumeGeniusSessionAfterBlankLeave(
  incomingSignedToken: string,
): boolean {
  if (!isGeniusShellResumePending()) return false;

  const persisted =
    readPersistedGeniusDeeplinkParams()?.signedToken ??
    getPriorPersistedTokenBeforePageLoad();
  if (persisted && persisted !== incomingSignedToken) {
    return false;
  }

  return true;
}

/**
 * True when Genius session is still open (OTP not completed).
 * Safe to call before auth store hydration (bootstrap / base-URL entry).
 */
export function shouldRedirectBaseUrlToGeniusDeeplink(): boolean {
  return (
    isGeniusDeeplinkAutoResumeEnabled() &&
    readPersistedGeniusDeeplinkParams() != null
  );
}

/**
 * Resume Genius destination until report OTP verification completes.
 * Matches Android `shouldAutoResumeGeniusDeeplink`.
 */
export function shouldAutoResumeGeniusDeeplink(
  profilesData?: ProfilesData | null,
): boolean {
  if (!isGeniusDeeplinkAutoResumeEnabled()) return false;
  if (!readPersistedGeniusDeeplinkParams()) return false;
  if (isGeniusMobileVerified(profilesData)) return false;
  return true;
}

/**
 * True only while Genius OTP / mobile verification is still required.
 * When the OTP modal is not shown, skip blank-on-back and use normal navigation.
 */
export function shouldEnforceGeniusOtpBackTrap(
  profilesData?: ProfilesData | null,
): boolean {
  if (!shouldRedirectBaseUrlToGeniusDeeplink()) return false;
  if (isGeniusMobileVerified(profilesData)) return false;

  return true;
}

/**
 * Arm Back → logout confirm while Genius deeplink nav lock is active or OTP
 * is still pending (starting-soon, instructions, exam, etc.).
 */
export function shouldEnforceGeniusBackTrap(
  profilesData?: ProfilesData | null,
): boolean {
  if (shouldEnforceGeniusBackTrapSync()) return true;
  return shouldEnforceGeniusOtpBackTrap(profilesData);
}

/** Storage-only — for sidebar / popstate before auth hydration. */
export function shouldEnforceGeniusBackTrapSync(): boolean {
  if (isGeniusDeeplinkNavLockActive()) return true;
  return (
    isGeniusDeeplinkAutoResumeEnabled() &&
    readPersistedGeniusDeeplinkParams() != null
  );
}

/**
 * Report OTP verified (or Genius flow abandoned) → dismiss ALL lock behavior:
 * blank-on-back, blank-on-URL-edit, base-URL auto-resume.
 * App returns to normal navigation.
 */
export function completeGeniusDeeplinkFlow(): void {
  // Keep the one-time signed token marker so re-opening the same link lands
  // on report with the existing session (never re-exchange → login failure).
  rememberConsumedSignedTokenFromSession();
  clearGeniusDeeplinkNavLock();
  // Report is normal flow now — never leave the Genius logout popup open.
  clearGeniusLogoutConfirmPending();
  markGeniusDeeplinkAutoResumeEnabled(false);
  releaseGeniusDeeplinkSession();
  notifyGeniusDeeplinkFlowDismissed();
}

export function releaseGeniusDeeplinkSession(): void {
  clearPersistedGeniusDeeplinkParams();
  resetActiveGeniusAuthToken();
  markGeniusFlowPhase("idle");
  markGeniusFlowContinued(false);
  markGeniusDeeplinkAutoResumeEnabled(false);
  resetGeniusUrlBootstrapTracking();
}
